// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Avatar,
    Body2,
    Button,
    Divider,
    makeStyles,
    Menu,
    MenuDivider,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { PersonFeedbackRegular, PersonFilled, QuestionCircleRegular, SettingsRegular } from '@fluentui/react-icons';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useMatch, useNavigate } from 'react-router-dom';
import { SignInContext } from '../../app';
import { getIsSignedIn, getUser, useConfig, useGithubStatus, UserContext } from '../../data';
import { CurriculumContext } from '../../data/curriculum-context';
import { AccountImage } from '../../icons/status';
import { TABLET_AND_BELOW, useAppStyles } from '../../styles';
import { IntlProps, TrackedButton, TrackedMenuItem } from '../common';
import LinkGithubDialog from '../curriculum-list/link-github-dialog';
import UnlinkGithubDialog from '../curriculum-list/unlink-github-dialog';
import DeleteAccountDialog from '../identity/delete-account-dialog';
import ReportDialog from '../report-dialog';
import AboutDialog from './about-dialog';
import BetaIndicator from './beta-indicator';
import DevToolsMenu from './dev-tools-menu';
import { InAppFeedbackContext } from './in-app-feedback';
import ThemeMenu from './theme-menu';

const borderStyle = `1px solid ${tokens.colorNeutralStroke2}`;
const useStyles = makeStyles({
    profile: {
        margin: tokens.spacingHorizontalS,
    },
    profileImage: {
        width: '4.68rem',
        height: '4.68rem',
        [TABLET_AND_BELOW]: {
            display: 'none',
        },
    },
    headerIcon: {
        border: borderStyle,
        '&:hover': {
            border: borderStyle,
        },
    },
});

// Header's far end group of controls (mostly related to identity).
export default injectIntl((props: IntlProps) => {
    const { intl } = props;
    const appStyles = useAppStyles();
    const styles = useStyles();
    const { curriculumAuthoring } = useConfig();
    const navigate = useNavigate();
    const [store, dispatch] = useContext(UserContext);
    const [linkGithubDialogOpen, setLinkGithubDialogOpen] = useState(false);
    const [unlinkGithubDialogOpen, setUnlinkGithubDialogOpen] = useState(false);
    const [_, curriculumDispatch] = useContext(CurriculumContext);
    const isSignedIn = getIsSignedIn(store);
    const user = getUser(store);
    const { ensureSignedIn } = useContext(SignInContext);
    const { isGithubLinked, githubLogin } = useGithubStatus();
    const [aboutOpen, setAboutOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const feedbackContext = useContext(InAppFeedbackContext);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const reportRoute = useMatch('/report');
    const feedbackRoute = useMatch('/feedback');

    useEffect(() => {
        if (reportRoute) {
            setReportDialogOpen(true);
        } else if (feedbackRoute) {
            feedbackContext.launch();
        }
    }, [feedbackContext, feedbackRoute, navigate, reportRoute]);

    const signInText = <FormattedMessage description="Header button text" defaultMessage="Sign in" />;

    // Settings menu items.
    const settingsItems = (
        <>
            <ThemeMenu />
            <DevToolsMenu asSubMenu />
        </>
    );

    // Feedback menu items.
    const feedbackItems = (
        <>
            {feedbackContext.enabled && (
                <TrackedMenuItem trackAction="menu-report-a-bug" onClick={() => feedbackContext.launch()}>
                    <FormattedMessage description="Header menu item text" defaultMessage="Give feedback" />
                </TrackedMenuItem>
            )}
            <TrackedMenuItem trackAction="menu-report-abuse" onClick={() => setReportDialogOpen(true)}>
                <FormattedMessage description="Header menu item text" defaultMessage="Report abuse" />
            </TrackedMenuItem>
        </>
    );

    // Help menu items.
    const helpItems = (
        <>
            <TrackedMenuItem trackAction="menu-faq" onClick={() => navigate('/faq')}>
                <FormattedMessage description="Header menu item text" defaultMessage="Frequently asked questions" />
            </TrackedMenuItem>
            <TrackedMenuItem trackAction="menu-about" onClick={() => setAboutOpen(true)}>
                <FormattedMessage description="Header menu item text" defaultMessage="About" />
            </TrackedMenuItem>
        </>
    );

    // Profile information and menu items for signed-in user.
    const signedInItems = (
        <>
            <div className={mergeClasses(appStyles.flexRowCentered, styles.profile)}>
                <AccountImage className={styles.profileImage} />
                <div>
                    <Body2 className={appStyles.alignCenter}>{user?.displayName}</Body2>
                    <div className={appStyles.textSmall}>{user?.providerId}</div>
                </div>
            </div>
            <Divider />
            {curriculumAuthoring &&
                isGithubLinked !== undefined &&
                (isGithubLinked ? (
                    <TrackedMenuItem trackAction="menu-unlink-github" onClick={() => setUnlinkGithubDialogOpen(true)}>
                        <FormattedMessage
                            description="Header menu item text"
                            defaultMessage="Unlink GitHub account ({LOGIN})"
                            values={{ LOGIN: githubLogin }}
                        />
                    </TrackedMenuItem>
                ) : (
                    <TrackedMenuItem trackAction="menu-link-github" onClick={() => setLinkGithubDialogOpen(true)}>
                        <FormattedMessage description="Header menu item text" defaultMessage="Link GitHub account" />
                    </TrackedMenuItem>
                ))}
            <Menu>
                <MenuTrigger>
                    <MenuItem>
                        <FormattedMessage description="Header menu item text" defaultMessage="Account management" />
                    </MenuItem>
                </MenuTrigger>
                <MenuPopover className={appStyles.menuPopover}>
                    <MenuList>
                        <TrackedMenuItem
                            trackAction="menu-download-data"
                            onClick={() => dispatch({ type: 'DownloadData' })}
                        >
                            <FormattedMessage description="Header menu item text" defaultMessage="Download data" />
                        </TrackedMenuItem>
                        <TrackedMenuItem
                            className={appStyles.menuDanger}
                            trackAction="menu-delete-account"
                            onClick={() => setConfirmDeleteOpen(true)}
                        >
                            <FormattedMessage description="Header menu item text" defaultMessage="Delete account" />
                        </TrackedMenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>
            <Divider />
            <TrackedMenuItem trackAction="menu-sign-out" onClick={() => dispatch({ type: 'SignOut' })}>
                <FormattedMessage description="Header menu item text" defaultMessage="Sign out" />
            </TrackedMenuItem>
        </>
    );

    const title = isSignedIn
        ? intl.formatMessage(
              { description: 'User avatar title', defaultMessage: 'Signed in as {name}' },
              { name: user?.displayName }
          )
        : intl.formatMessage({
              description: 'Sign-in menu title',
              defaultMessage: 'Sign in to access full functionality.',
          });

    return (
        <>
            {/* Display regular UI in desktop layouts. */}
            <div className={mergeClasses(appStyles.desktopOnly, appStyles.flexRowSmallGap)}>
                <Menu>
                    <MenuTrigger>
                        <Button
                            className={styles.headerIcon}
                            appearance="subtle"
                            icon={<SettingsRegular />}
                            title="Settings"
                        />
                    </MenuTrigger>
                    <MenuPopover className={appStyles.menuPopover}>
                        <MenuList>{settingsItems}</MenuList>
                    </MenuPopover>
                </Menu>
                <Menu>
                    <MenuTrigger>
                        <Button
                            className={styles.headerIcon}
                            appearance="subtle"
                            icon={<PersonFeedbackRegular />}
                            title="Provide feedback"
                        />
                    </MenuTrigger>
                    <MenuPopover className={appStyles.menuPopover}>
                        <MenuList>{feedbackItems}</MenuList>
                    </MenuPopover>
                </Menu>
                <Menu>
                    <MenuTrigger>
                        <Button
                            className={styles.headerIcon}
                            appearance="subtle"
                            icon={<QuestionCircleRegular />}
                            title="Help and support"
                        />
                    </MenuTrigger>
                    <MenuPopover className={appStyles.menuPopover}>
                        <MenuList>{helpItems}</MenuList>
                    </MenuPopover>
                </Menu>
                {isSignedIn && user && (
                    <Menu positioning="below-end">
                        <MenuTrigger>
                            <span>
                                <Avatar className={appStyles.cursorPointer} name={user.displayName} title={title} />
                            </span>
                        </MenuTrigger>
                        <MenuPopover className={appStyles.menuPopover}>
                            <MenuList>{signedInItems}</MenuList>
                        </MenuPopover>
                    </Menu>
                )}
                {!isSignedIn && (
                    <TrackedButton
                        trackAction="sign-in"
                        className={mergeClasses(appStyles.button, appStyles.buttonPrimary, appStyles.buttonMedium)}
                        onClick={() => ensureSignedIn('/my-work')}
                    >
                        {signInText}
                    </TrackedButton>
                )}
                <BetaIndicator />
            </div>
            {/* Collapse all controls into a corner menu on table and below. */}
            <div className={appStyles.desktopHidden}>
                <div className={appStyles.flexRowCentered}>
                    <Menu>
                        <MenuTrigger>
                            <Button appearance="subtle" icon={<PersonFilled />} title={title} />
                        </MenuTrigger>
                        <MenuPopover className={appStyles.menuPopover}>
                            <MenuList>
                                {!isSignedIn && (
                                    <TrackedMenuItem
                                        trackAction="menu-sign-in"
                                        onClick={() => ensureSignedIn('/my-work')}
                                    >
                                        {signInText}
                                    </TrackedMenuItem>
                                )}
                                {isSignedIn && signedInItems}
                                <MenuDivider />
                                {settingsItems}
                                <MenuDivider />
                                {feedbackItems}
                                <MenuDivider />
                                {helpItems}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    <BetaIndicator />
                </div>
            </div>

            <AboutDialog open={aboutOpen} onOpenChange={(_, { open }) => setAboutOpen(open)} />

            <DeleteAccountDialog
                open={confirmDeleteOpen}
                onOpenChange={(_, { open }) => setConfirmDeleteOpen(open)}
                onDownload={() => dispatch({ type: 'DownloadData' })}
                onDelete={() => dispatch({ type: 'DeleteAccount' })}
            />

            <LinkGithubDialog
                open={linkGithubDialogOpen}
                onOpenChange={(_, { open }) => setLinkGithubDialogOpen(open)}
                onConfirm={() => dispatch({ type: 'LinkGithubAccount', redirectUrl: '/authoring/redirect' })}
                onClose={() => curriculumDispatch({ type: 'Reload' })}
            />

            <UnlinkGithubDialog
                open={unlinkGithubDialogOpen}
                onOpenChange={(_, { open }) => setUnlinkGithubDialogOpen(open)}
                onConfirm={() => dispatch({ type: 'UnlinkGithubAccount' })}
            />

            <ReportDialog open={reportDialogOpen} onOpenChange={(_, { open }) => setReportDialogOpen(open)} />
        </>
    );
});
